import { getLandingPageUri } from '~/composables/craft'
import { useCacheStore } from '~/store/cache'

const hitsPerPage = 25

export const usePageOverviewStore = defineStore('page-overview', () => {
  const cacheStore = useCacheStore()
  const graphqlFetch = useGraphqlFetch()

  const state = {
    // Search context
    currentPage: ref(1),
    keyword: ref(''),
    orderBy: ref('relevance'),

    // Search results
    hits: ref<any[]>([]),
    totalHits: ref(0),
  }
  state.hitsPerPage = computed(() => hitsPerPage)
  state.urlParameters = computed(() => ({
    orderBy: state.orderBy.value === 'relevance' ? undefined : state.orderBy.value,
    search: state.keyword.value || undefined,
    page: state.currentPage.value === 1 ? undefined : state.currentPage.value,
  }))
  state.getHits = computed(() => state.hits.value.slice((state.currentPage.value - 1) * hitsPerPage, state.currentPage.value * hitsPerPage))

  // Initialize
  function initialize({ orderBy, page, search }: { orderBy?: string; page?: string; search?: string }) {
    // When there are no values given, i.e. from the URL, we will simply keep the most recently used search context
    if (orderBy === undefined && page === undefined && search === undefined) {
      return
    }

    state.orderBy.value = ['relevance', 'postDate'].includes(orderBy) ? orderBy : 'relevance'
    state.keyword.value = typeof search === 'string' ? search : ''
    state.currentPage.value = Math.max(1, typeof page === 'string' && !isNaN(Number.parseInt(page)) ? Number.parseInt(page) : 1)
  }

  // Search context
  function reset() {
    state.orderBy.value = 'relevance'
    state.keyword.value = ''
    state.currentPage.value = 1
    fetchPages()
  }

  function setOrderBy(orderBy: string) {
    state.orderBy.value = ['relevance', 'postDate'].includes(orderBy) ? orderBy : 'relevance'
    fetchPages()
  }

  function setCurrentPage(page: number) {
    state.currentPage.value = Math.max(1, typeof page === 'number' && !isNaN(page) ? page : 1)
    fetchPages()
  }

  function setKeyword(keyword: string) {
    state.keyword.value = typeof keyword === 'string' ? keyword : ''
    state.currentPage.value = 1
    fetchPages()
  }

  // Search results
  function setHits(hits: any[]) {
    state.hits.value = Array.isArray(hits) ? hits : []
  }

  function setTotalHits(totalHits: number) {
    state.totalHits.value = typeof totalHits === 'number' ? totalHits : 0
  }

  async function fetchPages() {
    const orderBy = state.orderBy.value
    const keyword = state.keyword.value
    const key = `page-overview;keyword:${keyword};orderBy:${orderBy}`
    const ttl = 300

    // Early return when no keywords are supplied
    if (!keyword) {
      setHits([])
      setTotalHits(0)
      return
    }

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query search($site:[String], $section:[String], $keyword:String, $orderBy:[String]) {
            elasticSearch(site:$site, section:$section, search:$keyword, orderBy:$orderBy, highlight: ["introduction", "description"]) {
              count
              hits(limit:1024) {
               title
               slug
               uri
               sectionHandle
               postDate
               highlights
              }
            }
          }`,
        variables: {
          keyword,
          orderBy: orderBy === 'postDate' ? ['postDate DESC'] : null,
          // don't use section "landing", as it only contains {"slug": "landing", "uri": "__home__"}
          section: ['article', 'articleOverview', 'department', 'jobOffer', 'overview', 'page', 'special', 'home', 'animalHome'],
          site: 'default',
        },
      })

      return {
        // When a 'department' is found we want to redirect it to the associated landing page
        hits: elasticSearch.hits.map((hit: any) => (hit.sectionHandle === 'department' ? { ...hit, uri: getLandingPageUri(hit.slug) } : hit)),
        totalHits: elasticSearch.count,
      }
    }

    // Fetch the hits
    const { hits, totalHits } = await cacheStore.fetch({ key, ttl, fetcher, fallback: { hits: [], totalHits: 0 } })
    setHits(hits)
    setTotalHits(totalHits)
  }

  return {
    ...state,
    initialize,
    reset,
    setOrderBy,
    setCurrentPage,
    setKeyword,
    setHits,
    setTotalHits,
    fetch: fetchPages,
  }
})
